import { useCopilotAction } from '@copilotkit/react-core';

import Pipeline from 'types/pipeline';

import { useAPI } from 'hooks/api/useAPI';

export function useListPipelinesTool() {
  const { applicationID, client } = useAPI();

  useCopilotAction({
    name: 'listPipelines',
    description:
      'List pipelines in the system. Use this to find pipeline IDs for creating deployments or understanding available workflows. Can filter by name.',
    parameters: [
      {
        name: 'ids',
        type: 'string',
        description: 'JSON array of pipeline IDs to fetch',
        required: false,
      },
      {
        name: 'containsName',
        type: 'string',
        description: 'Filter pipelines whose name contains this string',
        required: false,
      },
      {
        name: 'names',
        type: 'string',
        description: 'JSON array of exact pipeline names to search for',
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return. Defaults to 50.',
        required: false,
      },
    ],
    handler: async (args: {
      ids?: string | string[];
      containsName?: string;
      names?: string | string[];
      limit?: number;
    }) => {
      const parseArrayParam = (
        param: string | string[] | undefined
      ): string[] | undefined => {
        if (!param) return undefined;
        if (Array.isArray(param)) return param;
        try {
          return JSON.parse(param);
        } catch {
          // If it's not valid JSON, treat it as a single value
          return [param];
        }
      };

      try {
        const ids = parseArrayParam(args.ids);
        const names = parseArrayParam(args.names);

        const { data } = await client.get(`/apps/${applicationID}/pipelines`, {
          params: {
            pagination: 'offset',
            page: 1,
            limit: args.limit || 50,
            pipeline_ids: ids,
            contains_name: args.containsName,
            pipeline_names: names,
          },
        });

        const pipelines = data?.data || [];

        if (pipelines.length === 0) {
          return {
            message: 'No workflows found matching your criteria.',
            pipelines: [],
          };
        }

        return {
          message: `Found ${pipelines.length} pipeline(s)`,
          pipelines: pipelines.map((pipeline: Pipeline) => ({
            id: pipeline.id,
            name: pipeline.name,
            node_count: pipeline.definition?.length || 0,
            created_at: pipeline.created_at,
            updated_at: pipeline.updated_at,
          })),
        };
      } catch (error) {
        return {
          message: 'Failed to load pipelines',
          error: error instanceof Error ? error.message : 'Unknown error',
          pipelines: [],
        };
      }
    },
  });
}
